import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, CalendarClock } from "lucide-react";
import { api } from "../lib/api";
import { ClinqLogo } from "../components/ClinqLogo";

const fmtWhen = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Africa/Nairobi" });

export default function DiscoveryReschedule() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const load = () => api.get(`/public/discovery/reschedule/${token}`).then((r) => setInfo(r.data)).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/public/discovery/reschedule/${token}`, { slot });
      setDone(data.booking);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Something went wrong — please try again.");
      load();
    }
    setBusy(false);
  };

  const b = done || info?.booking;
  return (
    <div className="min-h-screen bg-[#F5F7F7] text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="discovery-reschedule-page">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 py-6">
        <Link to="/" data-testid="reschedule-logo-link"><ClinqLogo size="lg" tone="teal" /></Link>
        <Link to="/discovery" className="text-sm font-bold text-[#0A8B85] hover:underline">Discovery Sessions</Link>
      </header>
      <main className="mx-auto max-w-3xl px-5 pb-20">
        <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-10" data-testid="reschedule-card">
          {notFound ? (
            <p className="text-center text-sm text-neutral-600" data-testid="reschedule-not-found">We couldn't find that booking. Please check the link in your confirmation email or contact capital@clinq.africa.</p>
          ) : !info ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>
          ) : done ? (
            <div className="text-center" data-testid="reschedule-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-[#0A8B85]" />
              <p className="mt-4 text-xl font-extrabold text-[#0B2A28]">Your session has been moved</p>
              <p className="mt-2 text-sm text-neutral-600">New time: <span className="font-semibold text-[#0B2A28]">{fmtWhen(done.start_at)} EAT</span> · {done.format_label}</p>
              <p className="mt-3 text-sm text-neutral-600">An updated calendar invite is on its way to {done.email}. No extra payment is needed.</p>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#0A8B85]">Reschedule</p>
              <h1 className="mt-2 text-2xl font-extrabold text-[#0B2A28] sm:text-3xl">Choose a different slot</h1>
              <div className="mt-5 rounded-2xl bg-[#0B2A28] p-5 text-white" data-testid="reschedule-current">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#7BD3CE]">Your current session</p>
                <p className="mt-1.5 text-base font-bold">{b.full_name} · {b.business_name}</p>
                <p className="mt-1 text-sm text-[#B9D9D6]"><CalendarClock size={14} className="mr-1 inline" />{fmtWhen(b.start_at)} EAT · {b.format_label}</p>
              </div>
              {!info.can_reschedule ? (
                <p className="mt-6 text-sm text-neutral-600" data-testid="reschedule-locked">This session can no longer be rescheduled online. Please email capital@clinq.africa and we'll help.</p>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {info.slots.map((s) => (
                      <label key={s.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-sm transition-[border-color,background-color] ${!s.available ? "cursor-not-allowed opacity-50" : slot === s.key ? "border-[#0A8B85] bg-[#0A8B85]/5" : "border-neutral-300 hover:border-neutral-400"}`}>
                        <input type="radio" name="slot" value={s.key} disabled={!s.available} checked={slot === s.key} onChange={() => setSlot(s.key)} className="mt-0.5 accent-[#0A8B85]" data-testid={`reschedule-slot-${s.key}`} />
                        <span>
                          <span className="block font-semibold text-[#0B2A28]">{s.label}</span>
                          <span className="block text-xs text-neutral-500">{s.date_label}{s.available ? "" : " — already booked"}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {err && <p className="text-sm text-red-600" data-testid="reschedule-error">{err}</p>}
                  <button type="submit" disabled={busy || !slot} className="w-full rounded-full bg-[#0A8B85] px-6 py-3.5 text-sm font-extrabold text-white hover:bg-[#0C9F97] disabled:opacity-50" data-testid="reschedule-submit">
                    {busy ? "Moving your session…" : "Confirm new slot"}
                  </button>
                  <p className="text-center text-[11px] text-neutral-500">No extra payment. You and the Clinq Advisory team will receive an updated calendar invite.</p>
                </form>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
